import * as yup from "yup";
import stopWords from "../../assets/stopWords/stopWords.json";

/** ---------------- Shared regex/messages ---------------- */
const NAME_RX = /^[a-zA-Z0-9\s\/\-\,\.\']+$/; // letters (no diacritics), numbers, spaces, / - , . '
const NAME_RX_MSG =
  "must contain only letters (wo diacritics), numbers, spaces, slashes, dashes, commas, dots";


const objectSchema = (shape) =>
  yup.object(shape).transform((value) => (value == null ? {} : value));

// Missing UI flags are common for imported and previously saved houses.
// Require a street unless PO Box mode was explicitly selected or a PO Box exists.
const requiresStreet = (a, b, c, d, poBox) => {
  const mode = [a, b, c, d].find((value) => typeof value === "boolean");
  return mode ?? !poBox?.trim();
};

/** ---------------- Party schema (used in House) ---------------- */
const PartySchema = objectSchema({
  name: yup
      .string()
      .required("Name is required")
      .matches(NAME_RX, `Name ${NAME_RX_MSG}`)
      .max(35, "Name be at most 35 characters")
      .test(
        "not-a-stopword",
        "Name contains a prohibited term. Please use a valid name.",
        (value) => {
          if (!value) return true;
          const normalized = value.trim().toLowerCase();
          return !stopWords.PartyName.some(
            (word) => word.toLowerCase() === normalized,
          );
        },
      ),
    country: yup
      .object({
        name: yup.string().required("Country is required"),
      })
      .nullable()
      .required("Country is required"),
    city: objectSchema({
      cityName: yup.string().trim().required("City is required"),
    }).required("City is required"),
    postCode: yup
      .string()
      .required("Post code is required")
      .matches(/^\S*$/, "Post code cannot contain blank spaces"),

    // Address/PO Box toggles (optional flags carried in the UI/model)
    addressFlagConsignor: yup.boolean().optional(),
    addressFlagSeller: yup.boolean().optional(),
    addressFlagBuyer: yup.boolean().optional(),
    addressFlagConsignee: yup.boolean().optional(),

    address: yup
      .string()
      .when(
        [
          "addressFlagConsignor",
          "addressFlagSeller",
          "addressFlagBuyer",
          "addressFlagConsignee",
          "poBoxNumber",
        ],
        {
          is: requiresStreet,
          then: () =>
            yup
              .string()
              .trim()
              .required("Address is required")
              .max(30, "Address must be at most 30 characters")
              .matches(NAME_RX, `Address ${NAME_RX_MSG}`)
              .test(
                "not-a-stopword",
                "Address contains a prohibited or vague term. Please provide a valid address.",
                (value) => {
                  if (!value) return true; // required will catch empty
                  const normalized = value.trim().toLowerCase();
                  return !stopWords.PartyAddressOrCity.some(
                    (word) => word.toLowerCase() === normalized,
                  );
                },
              ),
          otherwise: () => yup.string().nullable(),
        },
      ),
    addressNumber: yup
      .string()
      .when(
        [
          "addressFlagConsignor",
          "addressFlagSeller",
          "addressFlagBuyer",
          "addressFlagConsignee",
          "poBoxNumber",
        ],
        {
          is: requiresStreet,
          then: () =>
            yup
              .string()
              .trim()
              .required("Address number is required")
              .max(5, "Address number must be at most 5 characters")
              .matches(NAME_RX, `Address number ${NAME_RX_MSG}`),

          otherwise: () => yup.string().nullable(),
        },
      ),
    poBoxNumber: yup
      .string()
      .when(
        [
          "addressFlagConsignor",
          "addressFlagSeller",
          "addressFlagBuyer",
          "addressFlagConsignee",
        ],
        {
          is: (a, b, c, d) => [a, b, c, d].some((v) => v === false),
          then: () => yup.string().trim().required("PO Box number is required"),
          otherwise: () => yup.string().nullable(),
        },
      ),

    eori: yup.string().nullable(),
  })
  .nullable()
  .required("Party details are required");

/** ---------------- Goods row schema ---------------- */
const GoodsRowSchema = yup.object({
  numberOfPackages: yup
    .number()
    .typeError("Number of packages is required")
    .required("Number of packages is required")
    .min(1, "Number of packages must be at least 1")
    .max(
      99999,
      "Number of packages is max 5 digits. Split into multiple goods if needed.",
    ),
  packageType: yup
    .object({
      code: yup.string().required("Package type code is required"),
      name: yup.string().required("Package type name is required"),
    })
    .nullable()
    .required("Package type is required"),
  hsCode: yup
    .string()
    .required("HS code is required")
    .matches(
      /^\d{6}(\d{2})?$/,
      "HS code must be exactly 6 or 8 digits, numbers only, no spaces or special characters",
    ),

  description: yup
    .string()
    .required("Description is required")
    .max(255, "Description must be at most 255 characters")
    .matches(NAME_RX, `Description ${NAME_RX_MSG}`)
    .test(
      "not-a-stopword",
      "Description contains a prohibited or vague term. Please use a more specific description.",
      (value) => {
        if (!value) return true; // skip if empty, will fail on required anyway
        const normalized = value.trim().toLowerCase();
        return !stopWords.GoodsDescription.some(
          (word) => word.toLowerCase() === normalized,
        );
      },
    ),
  weight: yup
    .number()
    .typeError("Gross weight must be a number")
    .required("Gross weight is required")
    .min(0, "Gross weight must be a positive number")
    .test(
      "is-valid-weight",
      "Gross weight must be a number with at most 6 decimal places",
      (value) => {
        if (value === undefined || value === null) return false;
        return /^\d+(\.\d{1,6})?$/.test(String(value));
      },
    ),
  dgCode: yup
    .string()
    .nullable()
    .test(
      "valid-dg-code",
      "DG code must be exactly four digits (0001–3999), no letters, spaces, or punctuation.",
      (value) => {
        if (!value) return true;

        // Must be exactly 4 digits
        if (!/^\d{4}$/.test(value)) return false;

        // Check numeric range
        const num = parseInt(value, 10);
        return num >= 1 && num <= 3999;
      },
    ),
});

/** ---------------- Places schema EU ---------------- */
const PlaceSchemaRequired = yup.object({
  countryName: yup.string().required("Country is required"),
  cityName: yup.string().trim().required("City is required"),
});

/** ---------------- House schema GB (new; refs moved here) ---------------- */
const HouseSchema = yup.object({
  mainTransportDocument: yup
    .string()
    .required("Main transport document is required"),

  mrnReference: yup
    .string()
    .required("House document reference is required")
    .matches(
      /^[a-zA-Z0-9]*$/,
      "House document reference must contain only alphanumeric characters",
    )
    .max(18, "House document reference must be at most 18 characters"),
  consignor: PartySchema.required(),
  seller: PartySchema.required(),
  buyer: PartySchema.required(),
  consignee: PartySchema.required(),

  goodsList: yup
    .array()
    .of(GoodsRowSchema)
    .min(1, "At least one goods row is required"),
});

/** ---------------- House schema EU (new; refs moved here) ---------------- */
const HouseSchemaEU = yup.object({
  mainTransportDocument: yup
    .string()
    .required("Main transport document is required"),

  mrnReference: yup
    .string()
    .required("House document reference is required")
    .matches(
      /^[a-zA-Z0-9]*$/,
      "House document reference must contain only alphanumeric characters",
    )
    .max(18, "House document reference must be at most 18 characters"),
  seller: PartySchema.required(),
  buyer: PartySchema.required(),
  goodsList: yup
    .array()
    .of(GoodsRowSchema)
    .min(1, "At least one goods row is required"),
  placeOfAcceptance: PlaceSchemaRequired.required(
    "Place of acceptance is required",
  ),
  placeOfDelivery: PlaceSchemaRequired.required(
    "Place of delivery is required",
  ),
  itineraryCountries: yup
    .array()
    .of(
      yup.object({
        code: yup.string().required(),
        name: yup.string().required(),
      }),
    )
    .min(1, "At least one itinerary country is required")
    .required("Itinerary countries are required"),
});

/** ---------------- Top-level consignment schema  GB ----------------
 * Validates the new shape with houses[]. Legacy root fields are migrated into
 * houses[0] inside the transform() so older payloads still pass validation.
 */
export const ConsignmentValidator = yup
  .object({
    customsOffice: yup
      .object({
        id: yup.string().required("Customs office is required"),
      })
      .nullable()
      .required("Customs office is required"),
    imoNumber: yup
      .string()
      .nullable()
      .when("customsOffice", {
        is: (co) => co?.imoRequired === true,
        then: (schema) =>
          schema.required("IMO Number is required for this customs office"),
        otherwise: (schema) => schema.notRequired(),
      }),
    approxArrivalDateAndTime: yup
      .date()
      .typeError("Approximate arrival date and time is required")
      .required("Approximate arrival date and time is required"),
    truckPlateNationality: yup
      .object({
        name: yup.string().required("Truck plate nationality is required"),
      })
      .nullable()
      .required("Truck plate nationality is required"),
    truckPlateNumber: yup
      .string()
      .required("Truck plate number is required")
      .test(
        "no-spaces",
        "Plate number should not contain blank spaces",
        (value) => !/\s/.test(value || ""),
      ),
    trailerPlateNumber: yup
      .string()
      .nullable()
      .test(
        "no-spaces",
        "Plate number should not contain blank spaces",
        (value) => !/\s/.test(value || ""),
      ),
    accompanied: yup.boolean().required("Accompanied status is required"),

    organization: yup.object({
      name: yup.string().required("Client is required"),
    }),
    carrier: yup
      .object({
        name: yup.string().required("Carrier is required"),
      })
      .nullable()
      .required("Carrier is required"),
    declarant: yup
      .object({
        name: yup.string().required("Declarant is required"),
      })
      .nullable()
      .required("Declarant is required"),
    // New canonical structure:
    houses: yup
      .array()
      .of(HouseSchema)
      .min(1, "At least one house is required"),

    // Legacy top-level refs are tolerated but not validated here; they’ll be migrated below.
  })
  .transform((currentValue) => {
    // If already new shape with houses[], keep as-is.
    if (Array.isArray(currentValue?.houses) && currentValue.houses.length > 0) {
      return currentValue;
    }

    // LEGACY → migrate root fields into a single house
    const migratedHouse = {
      mainTransportDocument: currentValue?.mainTransportDocument ?? "N990",
      // Bring over legacy per-consignment MRN/LRN/GGB into the first house
      mrnReference: currentValue?.mrnReference ?? null,

      consignor: currentValue?.consignor ?? {},
      seller: currentValue?.seller ?? {},
      buyer: currentValue?.buyer ?? {},
      consignee: currentValue?.consignee ?? {},
      goodsList:
        Array.isArray(currentValue?.goodsList) &&
        currentValue.goodsList.length > 0
          ? currentValue.goodsList
          : [
              {
                numberOfPackages: 0,
                packageType: null,
                hsCode: "",
                description: "",
                weight: 0,
              },
            ],
    };

    const cleaned = { ...currentValue };
    delete cleaned.mainTransportDocument;
    delete cleaned.mrnReference;
    delete cleaned.consignor;
    delete cleaned.seller;
    delete cleaned.buyer;
    delete cleaned.consignee;
    delete cleaned.goodsList;

    return {
      ...cleaned,
      houses: [migratedHouse],
    };
  });

/** ---------------- Top-level consignment schema  EU ----------------
 * Validates the new shape with houses[]. Legacy root fields are migrated into
 * houses[0] inside the transform() so older payloads still pass validation.
 */
export const ConsignmentValidatorEU = yup
  .object({
    customsOffice: yup
      .object({
        id: yup.string().required("Customs office is required"),
      })
      .nullable()
      .required("Customs office is required"),
    imoNumber: yup
      .string()
      .nullable()
      .when("customsOffice", {
        is: (co) => co?.imoRequired === true,
        then: (schema) =>
          schema.required("IMO Number is required for this customs office"),
        otherwise: (schema) => schema.notRequired(),
      }),
    approxArrivalDateAndTime: yup
      .date()
      .typeError("Approximate arrival date and time is required")
      .required("Approximate arrival date and time is required"),
    truckPlateNationality: yup
      .object({
        name: yup.string().required("Truck plate nationality is required"),
      })
      .nullable()
      .required("Truck plate nationality is required"),
    truckPlateNumber: yup
      .string()
      .required("Truck plate number is required")
      .test(
        "no-spaces",
        "Plate number should not contain blank spaces",
        (value) => !/\s/.test(value || ""),
      ),
    trailerPlateNumber: yup
      .string()
      .nullable()
      .test(
        "no-spaces",
        "Plate number should not contain blank spaces",
        (value) => !/\s/.test(value || ""),
      ),
    accompanied: yup.boolean().required("Accompanied status is required"),

    organization: yup.object({
      name: yup.string().required("Client is required"),
    }),
    carrier: yup
      .object({
        name: yup.string().required("Carrier is required"),
      })
      .nullable()
      .required("Carrier is required"),
    declarant: yup
      .object({
        name: yup.string().required("Declarant is required"),
      })
      .nullable()
      .required("Declarant is required"),
    // New canonical structure:
    houses: yup
      .array()
      .of(HouseSchemaEU)
      .min(1, "At least one house is required"),
    mrnReference: yup
      .string()
      .required("Master document reference is required")
      .matches(
        /^[a-zA-Z0-9]*$/,
        "Master document reference must contain only alphanumeric characters",
      )
      .max(18, "Master document reference must be at most 18 characters"),
    placeOfLoading: PlaceSchemaRequired.required(
      "Place of loading is required",
    ),

    placeOfUnloading: PlaceSchemaRequired.required(
      "Place of unloading is required",
    ),

    placeOfAcceptance: PlaceSchemaRequired.required(
      "Place of acceptance is required",
    ),

    placeOfDelivery: PlaceSchemaRequired.required(
      "Place of delivery is required",
    ),

    // Legacy top-level refs are tolerated but not validated here; they’ll be migrated below.
  })
  .transform((currentValue) => {
    // If already new shape with houses[], keep as-is.
    if (Array.isArray(currentValue?.houses) && currentValue.houses.length > 0) {
      return currentValue;
    }

    // LEGACY → migrate root fields into a single house
    const migratedHouse = {
      mainTransportDocument: currentValue?.mainTransportDocument ?? "N990",
      // Bring over legacy per-consignment MRN/LRN/GGB into the first house
      mrnReference: currentValue?.mrnReference ?? null,

      consignor: currentValue?.consignor ?? {},
      seller: currentValue?.seller ?? {},
      buyer: currentValue?.buyer ?? {},
      consignee: currentValue?.consignee ?? {},
      goodsList:
        Array.isArray(currentValue?.goodsList) &&
        currentValue.goodsList.length > 0
          ? currentValue.goodsList
          : [
              {
                numberOfPackages: 0,
                packageType: null,
                hsCode: "",
                description: "",
                weight: 0,
              },
            ],
    };

    const cleaned = { ...currentValue };
    delete cleaned.mainTransportDocument;
    delete cleaned.mrnReference;
    delete cleaned.consignor;
    delete cleaned.seller;
    delete cleaned.buyer;
    delete cleaned.consignee;
    delete cleaned.goodsList;

    return {
      ...cleaned,
      houses: [migratedHouse],
    };
  });
